import { EventBusy, ErrorOutline, Inventory2Outlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PropTypes from "prop-types";

// refetch devuelve una promesa que rechaza ante un error de red. El error ya se
// refleja en el estado de la consulta, así que acá sólo se evita que quede una
// promesa rechazada sin manejar.
const reintentar = (onReintentar) => () => {
  const resultado = onReintentar();
  if (resultado && typeof resultado.catch === "function") resultado.catch(() => {});
};

const Panel = ({ icono, titulo, detalle, onReintentar }) => (
  <Box
    sx={{
      display: "grid",
      placeItems: "center",
      gap: 1.25,
      textAlign: "center",
      py: 7,
      px: 3,
      borderRadius: "20px",
      bgcolor: "#fff",
      border: "1px solid #E2E8F0",
    }}
  >
    {icono}
    <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#172033" }}>{titulo}</Typography>
    <Typography sx={{ fontSize: 13.5, color: "#64748B", maxWidth: 460 }}>{detalle}</Typography>
    {onReintentar && (
      <Button
        variant="contained"
        // Sin argumentos: Button pasa el evento del click y refetch lo tomaria
        // como variables de la consulta.
        onClick={reintentar(onReintentar)}
        sx={{
          mt: 0.5,
          textTransform: "none",
          fontWeight: 800,
          borderRadius: "12px",
          px: 2.5,
          // El tema pinta los botones outlined con light.main, que sobre esta
          // tarjeta blanca queda ilegible; se fijan los colores de la pantalla.
          bgcolor: "#2C3087",
          color: "#fff",
          boxShadow: "none",
          "&:hover": { bgcolor: "#20245F", color: "#fff", boxShadow: "none" },
          "&:focus:not(:hover)": { bgcolor: "#2C3087", color: "#fff" },
        }}
      >
        Reintentar
      </Button>
    )}
  </Box>
);

Panel.propTypes = {
  icono: PropTypes.node,
  titulo: PropTypes.string.isRequired,
  detalle: PropTypes.string.isRequired,
  onReintentar: PropTypes.func,
};

// Un elemento JSX siempre es truthy, incluso si el componente devuelve null.
// El contenedor tiene que preguntar por el estado con esta función antes de
// decidir si renderiza el panel o el flujo de venta.
export const hayEstadoBloqueante = ({ loading, error, habilitado, sinCatalogo }) =>
  Boolean(loading || error || !habilitado || sinCatalogo);

// Estados en los que el punto de venta no puede operar.
export default function EstadoPuntoVenta({
  loading,
  error,
  habilitado,
  sinCatalogo,
  onReintentar,
}) {
  if (loading) {
    return (
      <Panel
        icono={<CircularProgress size={30} />}
        titulo="Cargando el punto de venta"
        detalle="Buscando el evento vigente y su catálogo."
      />
    );
  }

  if (error) {
    return (
      <Panel
        icono={<ErrorOutline sx={{ fontSize: 40, color: "#D32F2F" }} />}
        titulo="No se pudo cargar el punto de venta"
        detalle={error.message || "Ocurrió un error al consultar el evento vigente."}
        onReintentar={onReintentar}
      />
    );
  }

  if (!habilitado) {
    return (
      <Panel
        icono={<EventBusy sx={{ fontSize: 40, color: "#94A3B8" }} />}
        titulo="No hay un evento vigente"
        detalle="El punto de venta se habilita únicamente durante la vigencia de un evento de comedor."
        onReintentar={onReintentar}
      />
    );
  }

  if (sinCatalogo) {
    return (
      <Panel
        icono={<Inventory2Outlined sx={{ fontSize: 40, color: "#94A3B8" }} />}
        titulo="El evento no tiene productos cargados"
        detalle="Cargá el catálogo del evento para poder vender."
        onReintentar={onReintentar}
      />
    );
  }

  return null;
}

EstadoPuntoVenta.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  habilitado: PropTypes.bool,
  sinCatalogo: PropTypes.bool,
  onReintentar: PropTypes.func,
};
